import { Controller } from '@hotwired/stimulus'
import cytoscape from 'cytoscape'
import * as bootstrap from 'bootstrap'

export default class extends Controller {
  static targets = ['canvas']
  static values = {
    mapId: Number,
  }

  connect() {
    this.mode = 'normal'
    this.tempEdgeSource = null
    this.initCytoscape()
  }

  disconnect() {
    if (this.cy) {
      this.cy.destroy()
    }
  }

  initCytoscape() {
    this.cy = cytoscape({
      container: this.canvasTarget,
      multigraph: true,
      style: [
        {
          selector: 'node',
          style: {
            label: 'data(label)',
            'text-valign': 'center',
            'text-halign': 'center',
            'background-color': '#666',
            color: '#fff',
            'text-outline-width': 2,
            'text-outline-color': '#666',
            'font-size': '12px',
            width: 40,
            height: 40,
          },
        },
        {
          selector: 'node.point_of_interest',
          style: {
            'background-color': '#0d6efd',
            shape: 'round-rectangle',
          },
        },
        {
          selector: 'node.intersection',
          style: {
            'background-color': '#6c757d',
            shape: 'diamond',
          },
        },
        {
          selector: 'node.outdoor',
          style: {
            'border-width': 3,
            'border-color': '#198754',
          },
        },
        {
          selector: 'node.indoor',
          style: {
            'border-width': 3,
            'border-color': '#ffc107',
          },
        },
        {
          selector: 'edge',
          style: {
            width: 2,
            'line-color': '#999',
            'target-arrow-color': '#999',
            'target-arrow-shape': 'triangle',
            'curve-style': 'bezier',
            label: 'data(direction)',
            'font-size': '10px',
            'text-rotation': 'autorotate',
            'text-margin-y': -10,
          },
        },
        {
          selector: 'node:selected',
          style: {
            'background-color': '#ff0000',
            'border-width': 3,
            'border-color': '#ff0000',
          },
        },
        {
          selector: '.temp-edge',
          style: {
            'line-color': '#0d6efd',
            'line-style': 'dashed',
            'target-arrow-color': '#0d6efd',
          },
        },
      ],
      layout: {
        name: 'cose',
        animate: true,
        randomize: false,
        componentSpacing: 100,
        nodeRepulsion: 400000,
        edgeElasticity: 100,
        nestingFactor: 5,
        gravity: 80,
        numIter: 1000,
        initialTemp: 200,
        coolingFactor: 0.95,
        minTemp: 1.0,
      },
    })

    this.loadGraphData()
    this.setupEventHandlers()
  }

  loadGraphData() {
    fetch(`/api/maps/${this.mapIdValue}/graph_data`)
      .then((response) => response.json())
      .then((data) => {
        this.cy.add(data.nodes)
        this.cy.add(data.edges)
        this.cy.layout({ name: 'cose', tile: false }).run()
        this.updateStats()
      })
  }

  updateStats() {
    const placeCount = document.getElementById('place-count')
    const pathCount = document.getElementById('path-count')
    if (placeCount) placeCount.textContent = this.cy.nodes().length
    if (pathCount) pathCount.textContent = this.cy.edges().length
  }

  setupEventHandlers() {
    // Cytoscape events
    this.cy.on('tap', (evt) => {
      console.log(
        'Tap event detected, mode:',
        this.mode,
        'target:',
        evt.target === this.cy
      )
      if (evt.target === this.cy && this.mode === 'add-place') {
        // Clicked on canvas - show add place modal
        const position = evt.position
        console.log('Showing add place modal at position:', position)
        this.showAddPlaceModal(position)
      }
    })

    this.cy.on('tap', 'node', (evt) => {
      const node = evt.target

      if (this.mode === 'add-path') {
        if (!this.tempEdgeSource) {
          this.tempEdgeSource = node
          node.addClass('selected')
        } else if (this.tempEdgeSource.id() !== node.id()) {
          // Create path between nodes
          this.showAddPathModal(this.tempEdgeSource, node)
          this.tempEdgeSource.removeClass('selected')
          this.tempEdgeSource = null
        }
      } else if (this.mode === 'delete') {
        if (
          confirm(
            `Delete place "${node.data('label')}"? This will also delete all connected paths.`
          )
        ) {
          this.deletePlace(node.id())
        }
      }
    })

    this.cy.on('tap', 'edge', (evt) => {
      if (this.mode === 'delete') {
        const edge = evt.target
        if (confirm('Delete this path and its inverse?')) {
          this.deletePath(edge.id())
        }
      }
    })
  }

  // Mode control methods
  setAddPlaceMode() {
    this.mode = 'add-place'
    this.updateModeButtons()
  }

  setAddPathMode() {
    this.mode = 'add-path'
    this.tempEdgeSource = null
    this.updateModeButtons()
  }

  setDeleteMode() {
    this.mode = 'delete'
    this.updateModeButtons()
  }

  setNormalMode() {
    this.mode = 'normal'
    this.tempEdgeSource = null
    this.updateModeButtons()
  }

  resetLayout() {
    this.cy.layout({ name: 'cose', tile: false }).run()
  }

  updateModeButtons() {
    document.querySelectorAll('[data-mode-button]').forEach((btn) => {
      btn.classList.remove(
        'btn-primary',
        'btn-info',
        'btn-danger',
        'btn-secondary'
      )
      btn.classList.add('btn-outline-secondary')
    })

    const activeButton = document.querySelector(
      `[data-mode-button="${this.mode}"]`
    )
    if (activeButton) {
      activeButton.classList.remove('btn-outline-secondary')
      const buttonClass = {
        'add-place': 'btn-primary',
        'add-path': 'btn-info',
        delete: 'btn-danger',
        normal: 'btn-secondary',
      }[this.mode]
      activeButton.classList.add(buttonClass)
    }
  }

  // Modal methods
  showAddPlaceModal(position) {
    window.tempPosition = position
    const modal = new bootstrap.Modal(document.getElementById('addPlaceModal'))
    modal.show()
  }

  showAddPathModal(source, target) {
    window.tempPathSource = source
    window.tempPathTarget = target
    document.getElementById('path-from').textContent = source.data('label')
    document.getElementById('path-to').textContent = target.data('label')
    const modal = new bootstrap.Modal(document.getElementById('addPathModal'))
    modal.show()
  }

  savePlace() {
    const placeData = {
      place: {
        name: document.getElementById('place-name').value,
        map_id: this.mapIdValue,
        building_id: document.getElementById('place-building').value || null,
        floor: parseFloat(document.getElementById('place-floor').value) || 1,
        kind: document.querySelector('input[name="place-kind"]:checked').value,
        room_number: document.getElementById('place-room').value,
        description: document.getElementById('place-description').value,
      },
    }

    fetch('/api/places', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(placeData),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          // Add node to graph
          const node = {
            data: {
              id: data.place.id,
              label: data.place.label,
              building: data.place.building,
              floor: data.place.floor,
              kind: data.place.kind,
            },
            position: window.tempPosition,
            classes: [
              data.place.kind || 'default',
              placeData.place.building_id ? 'indoor' : 'outdoor',
            ].join(' '),
          }
          this.cy.add(node)
          this.updateStats()

          // Close modal and reset form
          bootstrap.Modal.getInstance(
            document.getElementById('addPlaceModal')
          ).hide()
          document.getElementById('add-place-form').reset()
        } else {
          alert('Error: ' + data.errors.join(', '))
        }
      })
  }

  savePath() {
    const pathData = {
      path: {
        origin_id: window.tempPathSource.id(),
        destination_id: window.tempPathTarget.id(),
        direction: document.getElementById('path-direction').value,
        weight: parseInt(document.getElementById('path-weight').value) || 1,
      },
    }

    fetch('/api/paths', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(pathData),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          // Add edge to graph
          const edge = {
            data: {
              id: data.path.id,
              source: data.path.source,
              target: data.path.target,
              direction: data.path.direction,
            },
          }
          this.cy.add(edge)

          // Add inverse edge if it was created
          if (data.path.inverse_id) {
            const inverseEdge = {
              data: {
                id: data.path.inverse_id,
                source: data.path.target,
                target: data.path.source,
                direction: this.getOppositeDirection(data.path.direction),
              },
            }
            this.cy.add(inverseEdge)
          }

          this.updateStats()

          // Close modal and reset form
          bootstrap.Modal.getInstance(
            document.getElementById('addPathModal')
          ).hide()
          document.getElementById('add-path-form').reset()
        } else {
          alert('Error: ' + data.errors.join(', '))
        }
      })
  }

  deletePlace(nodeId) {
    fetch(`/api/places/${nodeId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          this.cy.getElementById(nodeId).remove()
          this.updateStats()
        }
      })
  }

  deletePath(edgeId) {
    fetch(`/api/paths/${edgeId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          // Remove both the path and its inverse from the graph
          const edge = this.cy.getElementById(edgeId)
          const source = edge.source().id()
          const target = edge.target().id()

          // Remove this edge
          edge.remove()

          // Find and remove inverse edge
          this.cy.edges().forEach((e) => {
            if (e.source().id() === target && e.target().id() === source) {
              e.remove()
            }
          })

          this.updateStats()
        }
      })
  }

  getOppositeDirection(direction) {
    const opposites = {
      north: 'south',
      south: 'north',
      east: 'west',
      west: 'east',
      northeast: 'southwest',
      northwest: 'southeast',
      southeast: 'northwest',
      southwest: 'northeast',
      up: 'down',
      down: 'up',
    }
    return opposites[direction] || direction
  }
}
